import {
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Radio,
  Tooltip,
} from '@material-ui/core';
import {
  fetchOrderHistoryById,
  fetchOrdersHistory,
  setShoppingTabView,
  updateOrderInputValue,
} from '@src/reducers/shopify';
import {
  ShopifyOrderInputValue,
  ShopifyTabsView,
} from '@src/reducers/shopify/types';
import { getActiveChatContactInfo } from '@src/selectors/contact';
import {
  IGetContactInfo,
  getFetchOrderInputValue,
  getShopifyOrdersHistory,
  getShopifyOrdersHistoryIsLoading,
  getShopifyOrdersHistoryNoResults,
  getShopifyOrdersHistoryPageInfo,
} from '@src/selectors/shopify';
import { FetchOrdersHistoryType, ShoppingCartOrder } from '@src/types/shopify';
import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { OrderHistoryItem } from './OrderHistoryItem';
import { GoBackIcon, OrdersWarningIcon } from './SvgIcons';

export interface IOrdersHistory {
  labels: { [key: string]: string };
  orders: ShoppingCartOrder[];
  inputValue: FetchOrdersHistoryType;
  contactInfo: IGetContactInfo;
  isLoading: boolean;
  noResults: boolean;
  hasNextPage: boolean;
  handleSendOrderUrl: (id: string) => void;
  handleOpenOrderUrl: (id: string) => void;
  handleClickGoBack: () => void;
  handleUpdateOrderInputValue: (value: FetchOrdersHistoryType) => void;
  handleFetchOrders: () => void;
}

const OrdersHistoryWrapper: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { formatMessage } = intl;

  const inputValue = useSelector(getFetchOrderInputValue);
  const orders = useSelector(getShopifyOrdersHistory);
  const isLoading = useSelector(getShopifyOrdersHistoryIsLoading);
  const noResults = useSelector(getShopifyOrdersHistoryNoResults);
  const contactInfo = useSelector(getActiveChatContactInfo);
  const pageInfo = useSelector(getShopifyOrdersHistoryPageInfo);

  const { email, mobileNumber } = contactInfo;

  const labels = useMemo(
    () => ({
      title: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.title',
      }),
      byNumber: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.byNumber.label',
      }),
      byEmail: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.byEmail.label',
      }),
      orderNumber: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.number.label',
      }),
      orderDate: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.date.label',
      }),
      orderQuantity: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.quantity.label',
      }),
      orderTotalAmount: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.total.label',
      }),
      orderSubTotal: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.subTotal.label',
      }),
      orderTax: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.tax.label',
      }),
      orderShipping: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.shipping.label',
      }),
      orderState: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.state.label',
      }),
      sendUrlButton: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.sendUrl.button.label',
      }),
      notFound: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.notFound.error',
      }),
      PAID: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.state.PAID',
      }),
      PENDING: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.state.PENDING',
      }),
      CANCELLED: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.state.CANCELLED',
      }),
      helpText: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.help.search.text',
      }),
      helpTextNoEmailNumber: formatMessage({
        id: 'Shopify.shoppingCart.ordersHistory.order.help.not.email.or.number.text',
      }),
    }),
    [formatMessage]
  );

  const handleSendOrderUrl = (id: string) => {
    dispatch(fetchOrderHistoryById({ id, sendUrl: true }));
  };

  const handleOpenOrderUrl = (id: string) => {
    dispatch(fetchOrderHistoryById({ id }));
  };

  const handleClickGoBack = () => {
    dispatch(setShoppingTabView(ShopifyTabsView.PRODUCT_LIST));
  };

  const handleUpdateOrderInputValue = (value: FetchOrdersHistoryType) => {
    dispatch(updateOrderInputValue(value));
  };

  const handleFetchOrders = () => {
    dispatch(
      fetchOrdersHistory({
        value:
          inputValue === ShopifyOrderInputValue.EMAIL ? email : mobileNumber,
        type: inputValue,
        pageInfo,
      })
    );
  };

  useEffect(() => {
    dispatch(
      fetchOrdersHistory({
        value:
          inputValue === ShopifyOrderInputValue.EMAIL ? email : mobileNumber,
        type: inputValue,
        clean: true,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, mobileNumber, inputValue, dispatch]);

  return (
    <OrdersHistory
      labels={labels}
      orders={orders}
      contactInfo={contactInfo}
      inputValue={inputValue}
      isLoading={isLoading}
      noResults={noResults}
      hasNextPage={pageInfo?.hasNextPage}
      handleSendOrderUrl={handleSendOrderUrl}
      handleOpenOrderUrl={handleOpenOrderUrl}
      handleClickGoBack={handleClickGoBack}
      handleUpdateOrderInputValue={handleUpdateOrderInputValue}
      handleFetchOrders={handleFetchOrders}
    />
  );
};

export const OrdersHistory: React.FC<IOrdersHistory> = ({
  labels,
  orders,
  contactInfo,
  inputValue,
  isLoading,
  noResults,
  hasNextPage,
  handleClickGoBack,
  handleSendOrderUrl,
  handleOpenOrderUrl,
  handleUpdateOrderInputValue,
  handleFetchOrders,
}) => {
  const {
    title,
    byNumber,
    byEmail,
    notFound,
    helpText,
    helpTextNoEmailNumber,
  } = labels;
  const ordersListRef = useRef<HTMLElement | null>(null);
  const { email, mobileNumber } = contactInfo;

  const handleMoveScroll = () => {
    if (!ordersListRef.current) return;
    const { scrollHeight, offsetHeight, scrollTop } = ordersListRef.current;
    const height = scrollHeight - offsetHeight;
    const diff = height - Math.abs(scrollTop);
    const threshold = 200;
    if (diff <= threshold && hasNextPage) {
      handleFetchOrders();
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" margin="16px">
        <Box
          onClick={handleClickGoBack}
          style={{ cursor: 'pointer' }}
          data-testid="shopify-orders-history-goback-button"
        >
          {GoBackIcon}
        </Box>

        <Box
          fontSize={16}
          color="text.primary"
          fontWeight={500}
          textAlign="center"
          flex={2}
        >
          {title}
        </Box>
      </Box>

      <Box padding="8px 16px 8px 16px">
        <Box>
          <Divider style={{ height: '2px' }} />
          <Box
            fontSize={12}
            paddingTop={1}
            color="text.secondary"
            letterSpacing={-0.5}
          >
            {helpText}
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            paddingBottom={1}
            paddingTop={1}
          >
            <Tooltip title={!mobileNumber ? helpTextNoEmailNumber : ''}>
              <FormControlLabel
                value="bottom"
                control={
                  <Radio
                    color="primary"
                    checked={inputValue === ShopifyOrderInputValue.PHONE}
                    onClick={() =>
                      handleUpdateOrderInputValue(ShopifyOrderInputValue.PHONE)
                    }
                    style={{ padding: '2px 1px' }}
                    data-testid="shopify-orders-history-radio-input-phone"
                    disabled={!mobileNumber}
                  />
                }
                label={byNumber}
                labelPlacement="end"
              />
            </Tooltip>
            <Tooltip title={!email ? helpTextNoEmailNumber : ''}>
              <FormControlLabel
                value="bottom"
                control={
                  <Radio
                    color="primary"
                    checked={inputValue === ShopifyOrderInputValue.EMAIL}
                    onClick={() =>
                      handleUpdateOrderInputValue(ShopifyOrderInputValue.EMAIL)
                    }
                    style={{ padding: '2px 1px' }}
                    data-testid="shopify-orders-history-radio-input-email"
                    disabled={!email}
                  />
                }
                label={byEmail}
                labelPlacement="end"
              />
            </Tooltip>
          </Box>
          <Divider style={{ height: '2px' }} />
        </Box>

        <Box
          height="calc(100vh - 331px)"
          overflow="auto"
          {...{ ref: ordersListRef }}
          onScroll={handleMoveScroll}
        >
          {!isLoading && noResults && (
            <Box
              display="flex"
              alignItems="center"
              textAlign="center"
              justifyContent="center"
              flexDirection="column"
              height="100%"
            >
              <Box paddingBottom={3}>{OrdersWarningIcon}</Box>
              <Box fontWeight={500} fontSize={16}>
                {notFound}
              </Box>
            </Box>
          )}

          {!noResults && (
            <Box padding="8px 5px">
              {orders.map(item => (
                <OrderHistoryItem
                  key={item.orderNumber}
                  item={item}
                  labels={labels}
                  handleSendOrderUrl={() => handleSendOrderUrl(item.id)}
                  handleOpenOrderUrl={() => handleOpenOrderUrl(item.id)}
                />
              ))}
            </Box>
          )}
          {isLoading && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              paddingTop={10}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OrdersHistoryWrapper;
